import styled, { css, keyframes } from "styled-components";
import IconOwned from "ui-components/Icon";
const pulseLoading = keyframes`
  0%{
    left: -110%
  }
  10%{
    left: -110%
  }
  90%{
    left: 50% 
  }
  100%{
    left: 50% 
  }
`;

const stylesContainer = ({
  background,
  borderWidth,
  borderColor,
  fontColor
} = {}) => css`
  background: ${background};
  box-shadow: inset 0 0 0 ${borderWidth || 0}px ${borderColor};

  & g {
    fill: ${fontColor};
  }
`;

const stylesTitle = ({
  fontColor
} = {}) => css`
  color: ${fontColor};
`;

const Container = styled.button(({
  theme: {
    passive,
    hover,
    active,
    disabled,
    loading,
    size: {
      _padding: [paddingDefault, paddingMax],
      height,
      borderRadius
    },
    isLoading
  },
  hasCombo,
  justIcon,
  iconRight
}) => css`
    appearance: none;

    font-family: inherit;
    font-family: "OpenSans";

    position: relative;
    display: block;
    user-select: none;
    cursor: pointer;
    outline: none;
    margin: 0;
    text-decoration: none;

    ${!!borderRadius && `border-radius: ${`${borderRadius}px`};`}

    ${hasCombo && iconRight ? css`
          padding: 0 ${paddingDefault}px 0 ${paddingMax}px;
        ` : css`
          padding: 0 ${paddingDefault}px;
        `};

    ${justIcon && css`
      padding: 0;
    `}

    min-height: ${height}px;
    max-height: ${height}px;
    min-width: ${height}px;

    border: none;

    ${stylesContainer(passive)};

    &:disabled,
    &[aria-disabled="true"] {
      cursor: ${isLoading ? "wait" : "not-allowed"};

      ${!isLoading ? stylesContainer(disabled) : loading && css`
            ${stylesContainer(loading)}

            overflow: hidden;

            ::before {
              content: "";
              position: absolute;
              top: 0;
              left: -100%;
              width: 100%;
              height: 100%;
              ${!!borderRadius && `border-radius: ${`${borderRadius}px`};`}
              background: linear-gradient(
                  to right,
                  transparent 60%,
                  ${loading.color} 100%
                );

              animation: ${pulseLoading} 1.5s linear infinite;
            }
          `}
    }

    :focus:not(:disabled):not(:active):not([aria-disabled="true"]) {
      outline: none;

      ::after {
        content: "";
        position: absolute;
        top: -1px;
        left: -1px;
        right: -1px;
        bottom: -1px;
        ${!!borderRadius && `border-radius: ${`${borderRadius}px`};`}
        box-shadow: 0 0 0 3px rgba(1, 1, 1, 0.15);
      }
    }

    :hover:not(:disabled):not([aria-disabled="true"]) {
      ${stylesContainer(hover)}
    }

    :active:not(:disabled):not([aria-disabled="true"]) {
      ${stylesContainer(active)}
    }

    ::-moz-focus-inner {
      border: 0;
    }
  `);
const Icon = styled(IconOwned)(({
  theme: {
    passive,
    hover,
    active,
    disabled
  }
}) => css`
    fill: ${passive.fontColor};

    ${Container}:hover:not(:disabled):not([aria-disabled="true"]) & {
      fill: ${hover.fontColor};
    }

    ${Container}:active:not(:disabled):not([aria-disabled="true"]) & {
      fill: ${active.fontColor};
    }

    ${Container}:disabled &,
    ${Container}[aria-disabled="true"] & {
      fill: ${disabled.fontColor};
    }
  `);
const Title = styled.span(({
  theme: {
    passive,
    hover,
    active,
    disabled,
    size: {
      height,
      fontSize,
      fontWeight,
      textTransform
    },
    isLoading
  },
  isEllipsis = true
}) => css`
    font-family: inherit;
    display: block;
    padding: 0;
    font-size: ${fontSize}px;
    text-transform: ${textTransform};
    font-weight: ${fontWeight};
    line-height: ${height}px;

    ${isEllipsis && css`
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    `}

    ${stylesTitle(passive)};

    ${Container}:hover:not(:disabled):not([aria-disabled="true"]) & {
      ${stylesTitle(hover)}
    }

    ${Container}:active:not(:disabled):not([aria-disabled="true"]) & {
      ${stylesTitle(active)}
    }

    ${Container}:disabled &,
    ${Container}[aria-disabled="true"] & {
      ${!isLoading && stylesTitle(disabled)}
    }
  `);
const Wrapper = styled.span(({
  reverse,
  justTitle,
  sizeIcon = 18
}) => css`
    font-family: inherit;
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    flex-direction: ${reverse ? "row-reverse" : "row"};
    justify-content: center;

    ${reverse ? css`
          & > * + * {
            margin-right: 6px;
          }
        ` : css`
          & > * + * {
            margin-left: 6px;
          }
        `}

    > svg {
      min-width: ${sizeIcon}px;
      max-width: ${sizeIcon}px;
      min-height: ${sizeIcon}px;
      max-height: ${sizeIcon}px;
    }
  `);
const SimpleButton = styled.button(({
  theme: {
    passive,
    hover,
    active,
    disabled,
    loading,
    size: {
      _padding: [paddingDefault, paddingMax],
      height,
      borderRadius,
      fontSize,
      fontWeight,
      textTransform
    },
    isLoading,
    isEllipsis = true
  }
}) => css`
    appearance: none;

    font-family: inherit;
    font-family: "OpenSans";

    position: relative;
    display: block;
    user-select: none;
    cursor: pointer;
    outline: none;
    margin: 0;
    text-decoration: none;
    text-align: center;

    ${!!borderRadius && `border-radius: ${`${borderRadius}px`};`}

    padding: 0 ${paddingDefault}px;
    min-height: ${height}px;
    max-height: ${height}px;
    min-width: ${height}px;

    border: none;

    font-size: ${fontSize}px;
    text-transform: ${textTransform};
    font-weight: ${fontWeight};
    line-height: ${height}px;

    ${isEllipsis && css`
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    `}

    ${stylesContainer(passive)};
    ${stylesTitle(passive)};

    &:disabled,
    &[aria-disabled="true"] {
      cursor: ${isLoading ? "wait" : "not-allowed"};

      ${!isLoading ? stylesContainer(disabled) : loading && css`
            ${stylesContainer(loading)}

            overflow: hidden;

            ::before {
              content: "";
              position: absolute;
              top: 0;
              left: -100%;
              width: 100%;
              height: 100%;
              ${!!borderRadius && `border-radius: ${`${borderRadius}px`};`}
              background: linear-gradient(
                  to right,
                  transparent 60%,
                  ${loading.color} 100%
                );

              animation: ${pulseLoading} 1.5s linear infinite;
            }
          `}
      ${!isLoading && stylesTitle(disabled)}
    }

    :focus:not(:disabled):not(:active):not([aria-disabled="true"]) {
      outline: none;

      ::after {
        content: "";
        position: absolute;
        top: -1px;
        left: -1px;
        right: -1px;
        bottom: -1px;
        ${!!borderRadius && `border-radius: ${`${borderRadius}px`};`}
        box-shadow: 0 0 0 3px rgba(1, 1, 1, 0.15);
      }
    }

    :hover:not(:disabled):not([aria-disabled="true"]) {
      ${stylesContainer(hover)}
      ${stylesTitle(hover)}
    }

    :active:not(:disabled):not([aria-disabled="true"]) {
      ${stylesContainer(active)}
      ${stylesTitle(active)}
    }

    ::-moz-focus-inner {
      border: 0;
    }
  `);
export { Container, Title, Wrapper, Icon, SimpleButton };